import React, { useState, useMemo } from 'react';
import { Search, FileText, BarChart2, Clock, FileBox, Layers, ChevronDown, SlidersHorizontal } from 'lucide-react';
import { EntityType, Entity, NERViewerProps, SearchInType } from '../types';
import { EntityCard } from './EntityCard';
import { entityIcons } from '../utils/styles';
import { formatBytes, formatDate } from '../utils/formatters';

export const NERViewer: React.FC<NERViewerProps> = ({ analysisResults }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<'all' | EntityType>('all');
  const [searchIn, setSearchIn] = useState<SearchInType>('text');
  const [confidenceThreshold, setConfidenceThreshold] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  // Enhanced search and filter functionality
  const filteredEntities = useMemo(() => {
    return Object.entries(analysisResults.entities || {})
      .filter(([type]) => selectedType === 'all' || type === selectedType)
      .reduce((acc, [type, entities]) => {
        const filtered = entities.filter(entity => {
          if (entity.confidence < confidenceThreshold) {
            return false;
          }

          const searchTermLower = searchTerm.toLowerCase();
          if (!searchTermLower) return true;

          switch (searchIn) {
            case 'text':
              return entity.text.toLowerCase().includes(searchTermLower);
            case 'context':
              return entity.context.toLowerCase().includes(searchTermLower);
            case 'both':
              return entity.text.toLowerCase().includes(searchTermLower) ||
                     entity.context.toLowerCase().includes(searchTermLower);
            default:
              return true;
          }
        });

        if (filtered.length > 0) {
          acc[type] = filtered;
        }
        return acc;
      }, {} as Record<string, Entity[]>);
  }, [analysisResults.entities, selectedType, searchTerm, searchIn, confidenceThreshold]);

  // Calculate statistics
  const stats = useMemo(() => {
    const total = Object.values(filteredEntities)
      .reduce((sum, entities) => sum + entities.length, 0);
    
    const byType = Object.entries(filteredEntities)
      .reduce((acc, [type, entities]) => {
        acc[type] = entities.length;
        return acc;
      }, {} as Record<string, number>);

    const avgConfidence = Object.values(filteredEntities)
      .flat()
      .reduce((sum, entity) => sum + entity.confidence, 0) / (total || 1);

    return { total, byType, avgConfidence };
  }, [filteredEntities]);

  const renderIcon = (type: string) => {
    const IconComponent = entityIcons[type as EntityType] || entityIcons.default;
    return <IconComponent className="w-4 h-4 text-purple-400" />;
  };

  return (
    <div className="w-full space-y-6">
      {/* Document Overview - Responsive Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="glass-effect p-4 rounded-lg">
          <div className="flex items-center gap-3">
            <Layers className="w-5 h-5 text-purple-400 shrink-0" />
            <h3 className="text-sm font-medium text-purple-300 truncate">Total Entities</h3>
          </div>
          <p className="mt-2 text-2xl font-bold text-white">{stats.total}</p>
          <p className="mt-1 text-sm text-purple-400/60 truncate">
            {Object.keys(filteredEntities).length} types detected
          </p>
        </div>

        <div className="glass-effect p-4 rounded-lg">
          <div className="flex items-center gap-3">
            <BarChart2 className="w-5 h-5 text-purple-400 shrink-0" />
            <h3 className="text-sm font-medium text-purple-300 truncate">Confidence</h3>
          </div>
          <p className="mt-2 text-2xl font-bold text-white">
            {(stats.avgConfidence * 100).toFixed(1)}%
          </p>
          <div className="mt-2 h-1.5 bg-purple-900/50 rounded-full overflow-hidden">
            <div 
              className="h-full bg-purple-400 rounded-full transition-all duration-500"
              style={{ width: `${stats.avgConfidence * 100}%` }}
            />
          </div>
        </div>

        <div className="glass-effect p-4 rounded-lg">
          <div className="flex items-center gap-3">
            <FileBox className="w-5 h-5 text-purple-400 shrink-0" />
            <h3 className="text-sm font-medium text-purple-300 truncate">Document Size</h3>
          </div>
          <p className="mt-2 text-2xl font-bold text-white">
            {formatBytes(analysisResults.metadata.file_size)}
          </p>
          <p className="mt-1 text-sm text-purple-400/60 truncate">
            {analysisResults.pages.length} pages analyzed
          </p>
        </div>

        <div className="glass-effect p-4 rounded-lg">
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-purple-400 shrink-0" />
            <h3 className="text-sm font-medium text-purple-300 truncate">Analyzed</h3>
          </div>
          <p className="mt-2 text-2xl font-bold text-white truncate">
            {new Date(analysisResults.metadata.analysis_timestamp).toLocaleTimeString()}
          </p>
          <p className="mt-1 text-sm text-purple-400/60 truncate">
            {new Date(analysisResults.metadata.analysis_timestamp).toLocaleDateString()}
          </p>
        </div>
      </div>

      {/* Search and Filters - Responsive Layout */}
      <div className="glass-effect p-4 rounded-lg">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-purple-400/60 w-5 h-5" />
              <input
                type="text"
                placeholder="Search entities..."
                className="w-full pl-10 pr-4 py-2.5 bg-black/20 border border-purple-500/20 rounded-lg text-purple-100 placeholder-purple-400/60 focus:outline-none focus:ring-2 focus:ring-purple-500/40"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="flex gap-2">
              <select
                className="min-w-[150px] px-4 py-2.5 bg-black/20 border border-purple-500/20 rounded-lg text-purple-100 focus:outline-none focus:ring-2 focus:ring-purple-500/40"
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value as 'all' | EntityType)}
              >
                {['all', ...Object.keys(analysisResults.entities || {})].sort().map(type => (
                  <option key={type} value={type}>
                    {type === 'all' ? 'All Types' : type}
                  </option>
                ))}
              </select>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className="p-2.5 bg-black/20 border border-purple-500/20 rounded-lg text-purple-400 hover:text-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500/40"
                title="Advanced filters"
              >
                <SlidersHorizontal className="w-5 h-5" />
              </button>
            </div>
          </div>

          {showFilters && (
            <div className="flex flex-col lg:flex-row gap-4 pt-4 border-t border-purple-500/20">
              <div className="flex items-center gap-2">
                <span className="text-sm text-purple-300 whitespace-nowrap">Search in:</span>
                <select
                  className="w-full lg:w-auto px-3 py-1.5 bg-black/20 border border-purple-500/20 rounded-lg text-purple-100 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/40"
                  value={searchIn}
                  onChange={(e) => setSearchIn(e.target.value as SearchInType)}
                >
                  <option value="text">Entity Text</option>
                  <option value="context">Context</option>
                  <option value="both">Both</option>
                </select>
              </div>

              <div className="flex items-center gap-4 flex-1">
                <span className="text-sm text-purple-300 whitespace-nowrap">Min. Confidence:</span>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={confidenceThreshold}
                  onChange={(e) => setConfidenceThreshold(Number(e.target.value))}
                  className="flex-1 h-2 bg-black/20 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-purple-400"
                />
                <span className="text-sm text-purple-300 min-w-[4ch]">
                  {(confidenceThreshold * 100).toFixed(0)}%
                </span>
              </div>
            </div>
          )}

          {/* Search Summary */}
          <div className="text-sm text-purple-400/60">
            Found <span className="text-purple-300">{stats.total}</span> entities
            {searchTerm && <span className="text-purple-300"> matching "{searchTerm}"</span>}
            {selectedType !== 'all' && <span className="text-purple-300"> in category "{selectedType}"</span>}
          </div>
        </div>
      </div>

      {/* Results Grid - Responsive Layout */}
      <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-4">
        {Object.entries(filteredEntities).map(([type, entities]) => (
          <div key={type} className="glass-effect rounded-lg overflow-hidden">
            <div className="p-4 border-b border-purple-500/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {renderIcon(type)}
                  <h3 className="font-medium text-purple-100">{type}</h3>
                  <span className="text-sm text-purple-400/60">
                    ({entities.length})
                  </span>
                </div>
              </div>
            </div>
            <div className="p-4 space-y-3">
              {entities.map((entity, index) => (
                <EntityCard key={index} entity={entity} />
              ))}
            </div>
          </div>
        ))}
      </div>

      {Object.keys(filteredEntities).length === 0 && (
        <div className="glass-effect p-8 text-center">
          <Search className="w-12 h-12 text-purple-400/40 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-purple-100">No Results Found</h3>
          <p className="text-purple-400/60 mt-1">
            Try adjusting your search criteria or filters
          </p>
        </div>
      )}
    </div>
  );
};

export default NERViewer;